import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from "formik";
import { FormikInput, Button } from "../../../../../components/UI";
import Checkbox from '../../../../../components/UI/Checkbox';
import { NewIntegrationFormSchema, NewIntegrationFormSchemaWithAdobe } from '../../../../../schemas';
import { useUserContext } from '../../../../../contexts/UserContext';
import { getSpecificAdobeIoProduct, updateSpecificAdobeIoProduct, validateTokenAndWorkspace } from '../../../../../utils/APIs';
import Loader from '../../../../../components/UI/Loader/Loader';
import { permission } from '../../../../../config/permission';
import backwardArrow from '../../../../../assets/icons/backward-arrow.svg';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../../../../contexts/ContentfulContext';
import { Eye, EyeOff } from 'lucide-react';
const EditIntegration = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [scopes, setScopes] = useState([]);
    const [selectedScope, setSelectedScope] = useState("");
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [data, setData] = useState()
    const [, setOrganization] = useState({});
    const { userData, checkPermission, updateAdobeIoIntegrations, appData } = useUserContext();
    const { content } = useContentfulContext();

    const [loading, setLoading] = useState(false)
    const [showError, setShowError] = useState(false);
    const [showPassword, setShowPassword] = useState({
        client_key: false,
        x_api_key: false
    });
    const [, setIsValidated] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isValidationComplete, setIsValidationComplete] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [validationSuccess, setValidationSuccess] = useState("");

    const getProductData = async () => {
        try {
            setLoading(true);
            if (id && userData?.selectedOrganization?.org_token) {
                const result = await getSpecificAdobeIoProduct(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    id
                );
                setData(result?.data[0]);
                const orgData = result?.data[0].products.reduce((acc, curr) => {
                    acc[curr.name] = curr.organization;
                    return acc;
                }, {});
                setOrganization(orgData)
                setSelectedScope(result?.data[0]?.scope)
                setSelectedProducts(result?.data[0].products.map((x) => x.name));
            }
        } catch (error) {
            console.log("Error fetching product data:", error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        getProductData();
    }, [userData, id]);

    const validateFieldChanges = (fieldValues) => {
        const updatedProductsArray = data?.products.map(obj => {
            const { _id: _, ...rest } = obj;
            return rest;
        });

        const clientKey = fieldValues?.client_key;
        const xApiKey = fieldValues?.x_API_Key;

        if (clientKey == data?.client_key &&
            xApiKey == data?.x_API_Key &&
            data?.organization == fieldValues?.organization &&
            data?.client_name == fieldValues?.client_name &&
            data?.tenant_name == fieldValues?.tenant_name &&
            data?.customer_id == fieldValues?.customer_id &&
            data?.technical_account_id == fieldValues?.technical_account_id &&
            data?.technical_account_email == fieldValues?.technical_account_email &&
            JSON.stringify(updatedProductsArray) == JSON.stringify(fieldValues?.products)
        ) {
            return true;
        }
        return false;
    }

    const handleSave = async (values) => {
        if (!isValidationComplete) {
            toast.error("Please validate the integration first before submitting.");
            return;
        }

        try {
            const rawBodyData = {
                client_key: values.client_key,
                x_API_Key: values.x_api_key,
                organization: values.organization_name,
                scope: selectedScope,
                customer_id: values.customer_id,
                technical_account_id: values.technical_account_id,
                technical_account_email: values.technical_account_email,
                products: selectedProducts.map((product) => ({
                    name: product,
                })),
            };

            if (validateFieldChanges(rawBodyData)) return;

            const bodyData = rawBodyData;

            setLoading(true);
            if (data?.integration_name != values.integration_name) {
                bodyData.integration_name = values.integration_name
            }
            if (selectedProducts.includes("adobe_target")) {
                bodyData.client_name = values.client_name
                bodyData.tenant_name = values.tenant_name
            } else {
                bodyData.client_name = null
                bodyData.tenant_name = null
            }

            await updateSpecificAdobeIoProduct(
                userData.token,
                userData.selectedOrganization.org_token,
                { productId: id, bodyData }
            );

            !loading && toast.success(content.EDIT_INTEGRATION_CONTENT.success);
            updateAdobeIoIntegrations()
            navigate('/setup/adobeIO-integrations');
        } catch (error) {
            setShowError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (e, setFieldValue, setFieldTouched, validateForm) => {
        const value = e.target.value
        let updatedProducts

        if (e.target.checked) {
            updatedProducts = [...selectedProducts, value]
        } else {
            updatedProducts = selectedProducts.filter((item) => item !== value)
        }

        setSelectedProducts(updatedProducts)
        setFieldValue("products", updatedProducts)
        setFieldTouched("products", true, true)

        const mergedScope = getMergedScopes(updatedProducts)
        setSelectedScope(mergedScope)
        setFieldValue("scope", mergedScope)
        setFieldTouched("scope", true, true)

        setTimeout(() => validateForm(), 0)

        // Reset validation state when products change
        resetValidationState();
    }

    const getMergedScopes = (arr) => {
        return scopes
            ?.filter((item) => arr.includes(item.id))
            .flatMap((item) => item.scope.split(","))
            .filter((value, index, self) => value && self.indexOf(value) === index)
            .join(",")
    }

    useEffect(() => {
        getScopes()
    }, [])

    const getScopes = async () => {
        setScopes(appData?.scopes)
    }

    const handleChangeScope = (e, setFieldValue) => {
        const newScope = e.target.value
        setSelectedScope(newScope)
        setFieldValue("scope", newScope)

        // Reset validation state when scope changes
        resetValidationState();
    }

    // Add this helper function
    const resetValidationState = () => {
        setIsValidationComplete(false);
        setIsValidated(false);
        setValidationSuccess("");
        setValidationError("");
    };

    const toggleShowPassword = (field) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleValidate = async (values, { setSubmitting, setTouched, validateForm, setFieldValue }) => {
        try {
            setIsValidating(true);
            setValidationError(""); // Clear previous errors

            // Trigger all field validations by setting all fields as touched
            const allFields = Object.keys(values);
            const touchedFields = allFields.reduce((acc, field) => {
                acc[field] = true;
                return acc;
            }, {});
            setTouched(touchedFields);

            // Validate the form
            const errors = await validateForm(values);

            // If there are validation errors, stop here
            if (Object.keys(errors).length > 0) {
                toast.error("Please fix the form errors before validating.");
                setSubmitting(false);
                return;
            }

            // Prepare the Adobe IO data from form
            const adobeIOData = {
                integration_name: values.integration_name,
                client_key: values.client_key,
                x_API_Key: values.x_api_key,
                organization: values.organization_name,
                scope: selectedScope,
                technical_account_id: values.technical_account_id,
                technical_account_email: values.technical_account_email,
                products: selectedProducts.map((product) => ({
                    name: product,
                })),
            };

            if (selectedProducts.includes("adobe_target")) {
                adobeIOData.client_name = values.client_name;
                adobeIOData.tenant_name = values.tenant_name;
            }

            const result = await validateTokenAndWorkspace(
                userData.token,
                userData.selectedOrganization.org_token,
                adobeIOData.tenant_name || values.tenant_name,
                adobeIOData
            );

            if (result?.status) {
                setIsValidated(true);
                setIsValidationComplete(true);
                setValidationSuccess("Integration validated successfully. You can now submit the form.");
                setValidationError("");
                // Update customer_id from discovery API response
                if (result?.data?.customer_id) {
                    setFieldValue("customer_id", result.data.customer_id);
                }
            } else {
                setIsValidated(false);
                setIsValidationComplete(false);
                setValidationSuccess("");
                setValidationError("Invalid Adobe IO integration. Please check your inputs.");
            }
        } catch (error) {
            const errorMsg = error?.message || content?.COMMON_MESSAGE.something_wrong_msg;
            setIsValidated(false);
            setIsValidationComplete(false);
            setValidationSuccess("");

            if (errorMsg.includes("Token generation failed")) {
                setValidationError("Invalid Adobe IO integration: Token generation failed.");
            } else {
                setValidationError(errorMsg);
            }
        } finally {
            setIsValidating(false);
            setSubmitting(false);
        }
    };

    return (
        <div className="w-full px-4 md:px-8 my-6">
            {loading && <Loader />}
            {data && <div className="flex flex-col lg:w-1/2 justify-center items-start">
                <div className="space-y-4 md:space-y-6 w-full">
                    <a href="#" className="font-medium text-primary hover:underline flex items-center" onClick={(e) => {
                        e.preventDefault();
                        navigate('/setup/adobeIO-integrations')
                    }}>
                        <img src={backwardArrow || "/placeholder.svg"} alt='' className='w-5 h-5 mr-2' /> {content.EDIT_INTEGRATION_CONTENT.back_cta_text}
                    </a>
                    <Formik
                        initialValues={{
                            products: data?.products?.map((x) => x.name) || [],
                            integration_name: data?.integration_name || "",
                            client_key: data?.client_key || "",
                            x_api_key: data?.x_API_Key || "",
                            client_name: data?.client_name || "",
                            tenant_name: data?.tenant_name || "",
                            customer_id: data?.customer_id || "",
                            organization_name: data?.organization || "",
                            technical_account_id: data?.technical_account_id || "",
                            technical_account_email: data?.technical_account_email || "",
                            scope: data?.scope || "",
                        }}
                        validationSchema={selectedProducts.includes('adobe_target') ? NewIntegrationFormSchemaWithAdobe(content.NEW_INTEGRATION_CONTENT) : NewIntegrationFormSchema(content.NEW_INTEGRATION_CONTENT)}
                        onSubmit={handleSave}
                    >
                        {({ errors, touched, setFieldValue, values, setFieldTouched, validateForm, setTouched, setSubmitting }) => {
                            return <Form action="#" className="space-y-4">
                                <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">{content.EDIT_INTEGRATION_CONTENT.title}</h2>
                                <p>{content.EDIT_INTEGRATION_CONTENT.description}</p>

                                <FormikInput
                                    type="text"
                                    id="integration_name"
                                    name="integration_name"
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.integration_name}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.integration_name}
                                />

                                <FormikInput
                                    type={showPassword.client_key ? "text" : "password"}
                                    id="client_key"
                                    name="client_key"
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.client_key}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.client_key}
                                    onChange={() => resetValidationState()}
                                    suffixIcon={
                                        <button
                                            type="button"
                                            onClick={() => toggleShowPassword("client_key")}
                                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                            aria-label={showPassword.client_key ? "Hide client key" : "Show client key"}
                                        >
                                            {showPassword.client_key ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    }
                                />

                                <FormikInput
                                    type={showPassword.x_api_key ? "text" : "password"}
                                    id="x_api_key"
                                    name="x_api_key"
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.x_api_key}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.x_api_key}
                                    onChange={() => resetValidationState()}
                                    suffixIcon={
                                        <button
                                            type="button"
                                            onClick={() => toggleShowPassword("x_api_key")}
                                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                            aria-label={showPassword.x_api_key ? "Hide API key" : "Show API key"}
                                        >
                                            {showPassword.x_api_key ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    }
                                />

                                <FormikInput
                                    type="text"
                                    disabled
                                    id="customer_id"
                                    name="customer_id"
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.customer_id}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.customer_id}
                                />

                                <FormikInput
                                    type="text"
                                    id="organization_name"
                                    name="organization_name"
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.organization}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.organization}
                                />

                                <FormikInput
                                    type="text"
                                    id="scope"
                                    name="scope"
                                    value={selectedScope}
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.scope}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.scope}
                                    onChange={(e) => handleChangeScope(e, setFieldValue)}
                                />

                                <FormikInput
                                    type="text"
                                    id="technical_account_id"
                                    name="technical_account_id"
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.technical_account_id}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.technical_account_id}
                                />

                                <FormikInput
                                    type="text"
                                    id="technical_account_email"
                                    name="technical_account_email"
                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.technical_account_email}
                                    helperText={content.EDIT_INTEGRATION_CONTENT.help_text.technical_account_email}
                                />

                                <div className="mb-4">
                                    <div className="relative">
                                        <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-2">{content.EDIT_INTEGRATION_CONTENT.select_product.title}</h2>
                                        <p>{content.EDIT_INTEGRATION_CONTENT.select_product.description}</p>
                                        <ul className="flex flex-col w-full gap-4 justify-center my-4">
                                            {content.EDIT_INTEGRATION_CONTENT.select_product.products.map((item) => {
                                                return (
                                                    <li key={item.key} className="flex flex-col gap-3">
                                                        <label htmlFor={item.key} className="w-56 p-4 inline-flex items-center justify-center text-gray-500 bg-white border-2 border-gray-200 rounded-lg cursor-pointer peer-checked:border-primary hover:text-gray-600 peer-checked:text-gray-600 hover:bg-gray-50"
                                                            onChange={(e) => handleCheckboxChange(e, setFieldValue, setFieldTouched, validateForm)}>
                                                            <Checkbox elId={item.key} value={item.key} checked={values.products.includes(item.key)} className="hidden" selected={selectedProducts.indexOf(item.key) !== -1} />
                                                            <p className="text-md font-bold leading-tight tracking-tight text-black text-center ml-2">{item.name}</p>
                                                        </label>

                                                        {item.key === "adobe_target" && selectedProducts.includes(item.key) && (
                                                            <div className="space-y-4">
                                                                <FormikInput
                                                                    type="text"
                                                                    id="client_name"
                                                                    name="client_name"
                                                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.client_name}
                                                                    onChange={() => resetValidationState()}
                                                                />
                                                                <FormikInput
                                                                    type="text"
                                                                    id="tenant_name"
                                                                    name="tenant_name"
                                                                    label={content.EDIT_INTEGRATION_CONTENT.formLabels.tenant_name}
                                                                    onChange={() => resetValidationState()}
                                                                />
                                                            </div>
                                                        )}
                                                    </li>
                                                )
                                            })}
                                            {errors.products && touched.products ? <p className="mt-2 text-sm text-red-5">{errors.products}</p> : null}
                                        </ul>
                                    </div>
                                </div>
                                {showError && <p className="mt-2 text-sm text-red-5">{showError}</p>}
                                {validationSuccess && (
                                    <div className="p-2 text-green-700 rounded">
                                        ✓ {validationSuccess}
                                    </div>
                                )}
                                {validationError && (
                                    <div className="p-2 text-red-700 rounded">
                                        ✗ {validationError}
                                    </div>
                                )}

                                <div className="flex justify-start items-center gap-2 pt-2">
                                    {checkPermission(permission.EDIT_ADOBE_IO) && (
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={() => handleValidate(values, { setSubmitting, setTouched, validateForm, setFieldValue })}
                                            disabled={isValidationComplete || isValidating}
                                        >
                                            {isValidating ? content.NEW_INTEGRATION_CONTENT.validating_cta : content.NEW_INTEGRATION_CONTENT.validate_cta}
                                        </Button>
                                    )}
                                    {checkPermission(permission.EDIT_ADOBE_IO) && (
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={!isValidationComplete || loading}
                                            className={!isValidationComplete ? "opacity-50 cursor-not-allowed" : ""}
                                        >
                                            {loading ? content.NEW_INTEGRATION_CONTENT.submitting_cta : content.NEW_INTEGRATION_CONTENT.submit_cta}
                                        </Button>
                                    )}
                                </div>
                            </Form>
                        }}
                    </Formik>
                </div>
            </div>}
        </div>
    )
}

export default EditIntegration;